import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule, ButtonOptions } from '@usitsdasdesign/dds-ng/button';
import { InputModule, DescriptionType, TextAlign, InputOptions } from '@usitsdasdesign/dds-ng/input';
import {
  LabelPosition,
  Size,
  ErrorState,
  Themes,
  ExtThemes,
  ButtonKind,
  WidthState,
} from '@usitsdasdesign/dds-ng/shared';
import { UploaderModule, UploaderOptions, UploadFile } from '@usitsdasdesign/dds-ng/uploader';
import { SpecificationsService } from '../../core/services/specifications.service';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

@Component({
  selector: 'app-create-specification',
  standalone: true,
  imports: [ButtonModule, InputModule, ReactiveFormsModule, UploaderModule],
  templateUrl: './create-specification.component.html',
  styleUrl: './create-specification.component.scss',
})
export class CreateSpecificationComponent {
  projectId: string;
  uploadContent: string;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private specificationsService: SpecificationsService,
  ) {}

  inputFormCtrl = new FormControl({ value: '', disabled: false });

  inputOptions: InputOptions = {
    label: 'API specification name',
    labelPosition: LabelPosition.external,
    placeholder: '',
    isError: false,
    isRequired: false,
    isInverse: false,
    size: Size.md,
    description: '',
    descriptionType: DescriptionType.text,
    errorMessage: '',
    errorState: ErrorState.default,
    textAlign: TextAlign.left,
    type: 'text',
  };

  changeValue() {
    // Clear error state when user types
    if (this.inputOptions.isError) {
      this.inputOptions = {
        ...this.inputOptions,
        isError: false,
        errorMessage: '',
      };
    }
  }

  onBlur() {}

  uploadAreaOptions: UploaderOptions = {
    theme: Themes.dark,
    heading: '',
    description: 'Upload an OpenAPI .xml, .yaml, or .yml file',
    isDragAndDrop: true,
    isMultiple: false,
    maxSize: 2000,
    maxTotalSize: 5000,
    maxFiles: 1,
    maxSymbolsName: 200,
    maxSymbolsNameError: 200,
    acceptFormats: 'application/xml, text/xml, application/x-yaml, text/yaml, text/x-yaml',
    acceptExtensions: '.xml, .yaml, .yml',
    restrictedFormats: '',
    restrictedExtensions: '',
    exceptSymbols: '_',
    btnLabel: 'Choose file',
    chooseFileBtnKind: 'primaryLoud',
    isError: false,
    errorMessage: '',
    itemErrorMessage: '',
    itemSuccessMessage: '',
    unknownExtensionsIcon: 'dds-icon_clip',
    customClass: '',
    isDownloadable: false,
    allowFilesWithoutType: true,
    disabled: false,
    isRequired: false,
    isInverse: false,
  };

  fileList: UploadFile[] = [];

  addSpecificationButtonOptions: ButtonOptions = {
    theme: ExtThemes.green,
    kind: ButtonKind.primaryLoud,
    size: Size.md,
    width: WidthState.fixed,
    isLoading: false,
    icon: 'dds-icon_plus',
    isIconLeft: false,
    isColorBg: false,
    isInverse: false,
    disabled: false,
    ariaLabel: 'Add API Specification',
    customClass: '',
    role: 'button',
  };

  removedItem(item: UploadFile) {
    this.fileList = this.fileList.filter((file) => file.fileName !== item.fileName);
    this.uploadContent = '';

    // Clear any upload errors
    if (this.uploadAreaOptions.isError) {
      this.uploadAreaOptions = {
        ...this.uploadAreaOptions,
        isError: false,
        errorMessage: '',
      };
    }
  }

  uploadedItems(items: UploadFile[]) {
    if (!items || items.length === 0) {
      console.error('No files uploaded');
      return;
    }

    const uploadedFile = items[0];

    if (!uploadedFile.file) {
      console.error('Invalid file object');
      return;
    }

    // Clear any previous errors
    if (this.uploadAreaOptions.isError) {
      this.uploadAreaOptions = {
        ...this.uploadAreaOptions,
        isError: false,
        errorMessage: '',
      };
    }

    const reader = new FileReader();

    reader.onloadend = () => {
      this.uploadContent = reader.result?.toString() ?? '';
      console.log('File loaded successfully:', uploadedFile.fileName);
    };

    reader.onerror = (error) => {
      console.error('Error reading file:', error);
      this.uploadAreaOptions = {
        ...this.uploadAreaOptions,
        isError: true,
        errorMessage: 'Failed to read file. Please try again.',
      };
    };

    //only 1 file allowed
    reader.readAsText(uploadedFile.file);
  }

  addSpecificationClick(): void {
    // Validate that we have the required data
    if (!this.inputFormCtrl.value || this.inputFormCtrl.value.trim() === '') {
      console.error('Specification name is required');
      this.inputOptions = {
        ...this.inputOptions,
        isError: true,
        errorMessage: 'Please enter a specification name',
      };
      return;
    }

    if (!this.uploadContent || this.uploadContent.trim() === '') {
      console.error('Specification content is required');
      this.uploadAreaOptions = {
        ...this.uploadAreaOptions,
        isError: true,
        errorMessage: 'Please upload a specification file',
      };
      return;
    }

    this.specificationsService
      .createSpecification(this.projectId, {
        name: this.inputFormCtrl.value ?? '',
        description: '', // TODO: extract description & version from content
        content: this.uploadContent,
        version: '0.0.3',
      })
      .subscribe({
        next: (e) => {
          console.log('Specification created successfully:', e);
          this.router.navigate(['../api-specifications'], { relativeTo: this.route });
        },
        error: (err) => {
          console.error('Error creating specification:', err);
          this.uploadAreaOptions = {
            ...this.uploadAreaOptions,
            isError: true,
            errorMessage: 'Failed to create specification. Please try again.',
          };
        },
      });
  }

  cancelButtonOptions: ButtonOptions = {
    theme: ExtThemes.green,
    kind: ButtonKind.primary,
    size: Size.md,
    width: WidthState.fixed,
    isLoading: false,
    icon: '',
    isIconLeft: true,
    isColorBg: false,
    isInverse: false,
    disabled: false,
    ariaLabel: 'Cancel',
    customClass: '',
    role: 'button',
  };

  cancelClick(event: Event): void {
    this.router.navigate(['../api-specifications'], { relativeTo: this.route });
  }

  ngOnInit(): void {
    this.route.parent?.paramMap.subscribe((params) => {
      this.projectId = params.get('id') ?? '';
    });
  }
}
